import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePageSize from "@/hooks/usePageSize";
import usePopupStore from "@/hooks/usePopupStore";
import useSearchFilters from "@/hooks/useSearchFilters";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import { FilterBool, FilterSelect } from "@/UI/inputs/FilterSelect";
import FilterRange from "@/UI/inputs/FilterRange";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import ChallengesForm from "@/UI/forms/ChallengesForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import SidePopup from "@/UI/popups/SidePopup";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

interface Challenge {
    _id: string;
    name: string;
    description: string;
    trigger: "money" | "order" | "product" | "category";
    target: {
        _id: string;
        name: string;
        picture: string;
    } | null;
    count: number;
    days: number | null;
    consecutive: boolean;
    startDate: string;
    endDate: string;
    minPrice: number | null;
    tiers: any[];
    coffeeBeans: number;
    repeatable: boolean;
    type: "system" | "store";
    active: boolean;
}

export default function ChallengesScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const { pageSize, setPageSize } = usePageSize();

    const filters = useSearchFilters({
        search: undefined as string | undefined,
        trigger: [] as string[],
        active: [] as string[],
        repeatable: [] as string[],
        start_date_from: undefined as string | undefined,
        start_date_to: undefined as string | undefined,
        end_date_from: undefined as string | undefined,
        end_date_to: undefined as string | undefined,
        min_coffee_beans: undefined as string | undefined,
        max_coffee_beans: undefined as string | undefined,
        tiers: [] as string[],
    });

    // ── Fields fetch ────────────────────────────────────────────────────────────
    const { data: fieldsData } = useFetch("challenges/fields");
    const triggerOptions: { title: string; key: string }[] = fieldsData?.data?.triggers ?? [];
    const tierOptions: { _id: string; name: string | { lang: string; value: string }[] }[] = fieldsData?.data?.tiers ?? [];

    // ── Challenges fetch ─────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("challenges", { query: { page: 1, limit: pageSize, ...filters.committedQuery } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...filters.committedQuery } });
        };
        window.addEventListener("challenges:refresh", handler);
        return () => window.removeEventListener("challenges:refresh", handler);
    }, []);

    const challenges: Challenge[] = data?.data?.challenges ?? [];
    const total: number = data?.data?.total ?? 0;

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
        invoke({ query: { page: 1, limit: size, ...filters.committedQuery } });
    };

    // ── Delete fetch ────────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("challenges", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (challenge: Challenge) => {
        const name = "confirm-delete-challenge";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.challenges.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(challenge._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Get-one fetch (duplicate) ────────────────────────────────────────────
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("challenges", { method: "GET" }, false);

    const openDuplicateForm = (challenge: any) => {
        const name = "duplicate-challenge";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.challengeForm.duplicateTitle,
                Content: ChallengesForm,
                contentDetails: {
                    initialData: challenge,
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: pageSize, ...filters.committedQuery } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const challenge = oneData?.data?.challenge;
            if (challenge) openDuplicateForm(challenge);
        }
    }, [fetchingOne]);

    const handleDuplicate = (row: Challenge) => {
        fetchOne({ param: row._id });
    };

    // ── Add Challenge ────────────────────────────────────────────────────────────
    const openAddChallengeForm = () => {
        const name = "add-challenge";
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title: t.challengeForm.createTitle,
                Content: ChallengesForm,
                contentDetails: {
                    onSuccess: () => {
                        window.dispatchEvent(new CustomEvent("challenges:refresh"));
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Helper Functions ────────────────────────────────────────────────────────
    const formatDate = (dateString: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    // ── Columns ─────────────────────────────────────────────────────────────────
    const columns: TableColumn<Challenge>[] = [
        {
            key: "name",
            header: t.challenges.col.name,
            width: "180px",
            render: (row) => <TableCell.Text value={row.name || "—"} maxChars={25} />,
        },
        {
            key: "trigger",
            header: t.challenges.col.trigger,
            width: "100px",
            render: (row) => <TableCell.Badge label={t.challenges.triggers[row.trigger]} variant="orange" />,
        },
        {
            key: "target",
            header: t.challenges.col.target,
            width: "140px",
            render: (row) => {
                if (row.trigger === "product" || row.trigger === "category") {
                    return <TableCell.Text value={row.target?.name || "—"} maxChars={20} />;
                }
                return <TableCell.Text value="—" />;
            },
        },
        {
            key: "coffeeBeans",
            header: t.challenges.col.coffeeBeans,
            width: "100px",
            render: (row) => (
                <span className="challenges-points-badge">
                    {row.coffeeBeans.toLocaleString()} {t.challenges.beans}
                </span>
            ),
        },
        {
            key: "dateRange",
            header: t.challenges.col.dateRange,
            width: "180px",
            render: (row) => (
                <div className="challenges-date-range">
                    <span>{formatDate(row.startDate)}</span>
                    <span className="challenges-date-separator">{lang === "ar" ? "←" : "→"}</span>
                    <span>{formatDate(row.endDate)}</span>
                </div>
            ),
        },
        {
            key: "count",
            header: t.challenges.col.count,
            width: "80px",
            render: (row) => <span className="challenges-count-text">{row.count}x</span>,
        },
        {
            key: "active",
            header: t.challenges.col.active,
            width: "100px",
            render: (row) => (
                <span className={`challenges-badge ${row.active ? "challenges-badge--active" : "challenges-badge--inactive"}`}>
                    {row.active ? t.challenges.active : t.challenges.inactive}
                </span>
            ),
        },
        {
            key: "actions",
            header: t.challenges.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    const filterContent = (
        <>
            <FilterSearchInput
                value={filters.draft.search}
                onChange={(v) => filters.setDraftField("search", v)}
                placeholder={t.filters.challenges.search}
            />
            {triggerOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.challenges.trigger}
                    options={triggerOptions.map((o) => ({ value: o.key, label: o.title }))}
                    value={filters.draft.trigger as string[]}
                    onChange={(v) => filters.setDraftField("trigger", v)}
                />
            )}
            <FilterBool
                label={t.filters.challenges.active}
                value={filters.draft.active as string[]}
                onChange={(v) => filters.setDraftField("active", v)}
                yesLabel={t.challenges.active}
                noLabel={t.challenges.inactive}
            />
            <FilterBool
                label={t.filters.challenges.repeatable}
                value={filters.draft.repeatable as string[]}
                onChange={(v) => filters.setDraftField("repeatable", v)}
                yesLabel={t.filters.yes}
                noLabel={t.filters.no}
            />
            <FilterRange
                label={t.filters.challenges.startDate}
                fromValue={filters.draft.start_date_from}
                toValue={filters.draft.start_date_to}
                onFromChange={(v) => filters.setDraftField("start_date_from", v)}
                onToChange={(v) => filters.setDraftField("start_date_to", v)}
                type="date"
            />
            <FilterRange
                label={t.filters.challenges.endDate}
                fromValue={filters.draft.end_date_from}
                toValue={filters.draft.end_date_to}
                onFromChange={(v) => filters.setDraftField("end_date_from", v)}
                onToChange={(v) => filters.setDraftField("end_date_to", v)}
                type="date"
            />
            <FilterRange
                label={t.filters.challenges.beansRange}
                fromValue={filters.draft.min_coffee_beans}
                toValue={filters.draft.max_coffee_beans}
                onFromChange={(v) => filters.setDraftField("min_coffee_beans", v)}
                onToChange={(v) => filters.setDraftField("max_coffee_beans", v)}
                fromPlaceholder={t.filters.from}
                toPlaceholder={t.filters.to}
            />
            {tierOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.challenges.tiers}
                    options={tierOptions.map((o) => ({
                        value: o._id,
                        label: typeof o.name === "string" ? o.name : (o.name.find((n) => n.lang === lang)?.value ?? o.name[0]?.value ?? o._id),
                    }))}
                    value={filters.draft.tiers as string[]}
                    onChange={(v) => filters.setDraftField("tiers", v)}
                />
            )}
        </>
    );

    return (
        <div className="challenges-screen">
            <ActionsBar
                onAdd={openAddChallengeForm}
                filterContent={filterContent}
                filterOpen={filters.filterOpen}
                onFilterOpen={filters.openFilter}
                onFilterSave={() =>
                    filters.saveFilter((q) => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize, ...q } });
                    })
                }
                onFilterClear={() =>
                    filters.clearFilter(() => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize } });
                    })
                }
                onFilterCancel={filters.cancelFilter}
                hasActiveFilters={filters.hasActiveFilters}
            />
            <div className="challenges-screen-content">
                <Table
                    columns={columns}
                    data={challenges}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize, total, onPageChange: setPage, onPageSizeChange: handlePageSizeChange }}
                />
            </div>
        </div>
    );
}
